import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import useItemStore from '@store/useItemStore'
import itemService from '@services/itemService'
import { Modal, Button } from '@components/common'
import { Edit2, History, Users, Download, FileText, Calendar, User, File, HardDrive } from 'lucide-react'

/**
 * ItemView Component
 *
 * Modal for viewing item details, file info, and quick actions.
 */
const ItemView = ({ onClose, onEdit, onVersions, onAssign }) => {
  const { selectedItem, fetchItem, downloadItem, loading } = useItemStore()

  useEffect(() => {
    if (selectedItem?.id) {
      fetchItem(selectedItem.id)
    }
  }, [selectedItem?.id])

  if (!selectedItem) {
    return null
  }

  const getTypeColor = (type) => {
    const colors = {
      policy: '#3b82f6',
      procedure: '#10b981',
      template: '#8b5cf6',
      guideline: '#f59e0b',
      resource: '#6366f1',
    }
    return colors[type] || '#6b7280'
  }

  const getStatusBadge = (status) => {
    const badges = {
      draft: <span className="badge badge--draft">Draft</span>,
      published: <span className="badge badge--published">Published</span>,
      archived: <span className="badge badge--archived">Archived</span>,
    }
    return badges[status] || null
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  /**
   * Get user-friendly file type from MIME type
   */
  const getFileTypeDisplay = (mimeType) => {
    const mimeMap = {
      'application/pdf': 'PDF Document',
      'application/msword': 'Word Document (.doc)',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document (.docx)',
    }
    return mimeMap[mimeType] || mimeType
  }

  const handleDownload = () => {
    downloadItem(selectedItem.id)
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Item Details"
      size="large"
      closeOnBackdrop={false}
    >
      <div className="item-view">
        {/* Header */}
        <div className="item-view__header">
          <div
            className="item-view__icon"
            style={{
              backgroundColor: `${getTypeColor(selectedItem.item_type)}20`,
              color: getTypeColor(selectedItem.item_type),
            }}
          >
            <FileText size={24} />
          </div>
          <div className="item-view__title-section">
            <h2 className="item-view__title">{selectedItem.title}</h2>
            <div className="item-view__badges">
              <span
                className="type-badge"
                style={{
                  backgroundColor: `${getTypeColor(selectedItem.item_type)}20`,
                  color: getTypeColor(selectedItem.item_type),
                }}
              >
                {selectedItem.item_type.charAt(0).toUpperCase() + selectedItem.item_type.slice(1)}
              </span>
              {getStatusBadge(selectedItem.status)}
              <span className="version-badge">v{selectedItem.current_version}</span>
            </div>
          </div>
        </div>

        {/* Description */}
        {selectedItem.description && (
          <div className="detail-section">
            <h3 className="detail-section__title">Description</h3>
            <p className="item-view__description">{selectedItem.description}</p>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="detail-section">
          <h3 className="detail-section__title">Statistics</h3>
          <div className="stats-cards">
            <div className="stats-card">
              <div className="stats-card__icon stats-card__icon--primary">
                <Users size={20} />
              </div>
              <div className="stats-card__content">
                <div className="stats-card__value">{selectedItem.assignment_count || 0}</div>
                <div className="stats-card__label">Assigned Companies</div>
              </div>
            </div>
            <div className="stats-card">
              <div className="stats-card__icon stats-card__icon--secondary">
                <History size={20} />
              </div>
              <div className="stats-card__content">
                <div className="stats-card__value">{selectedItem.version_count || 1}</div>
                <div className="stats-card__label">Total Versions</div>
              </div>
            </div>
          </div>
        </div>

        {/* File Information */}
        <div className="detail-section">
          <h3 className="detail-section__title">File Information</h3>
          <div className="file-info-card">
            <div className="file-info-card__icon">
              <File size={24} />
            </div>
            <div className="file-info-card__details">
              <div className="file-info-card__name">{selectedItem.original_name}</div>
              <div className="file-info-card__meta">
                <span><HardDrive size={12} /> {itemService.formatFileSize(selectedItem.file_size)}</span>
                <span className="file-info-card__separator">•</span>
                <span>{getFileTypeDisplay(selectedItem.mime_type)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="detail-section">
          <h3 className="detail-section__title">Metadata</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item__label">
                <Calendar size={14} /> Created
              </span>
              <span className="detail-item__value">{formatDate(selectedItem.created_at)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">
                <Calendar size={14} /> Last Updated
              </span>
              <span className="detail-item__value">{formatDate(selectedItem.updated_at)}</span>
            </div>
            {selectedItem.creator && (
              <div className="detail-item">
                <span className="detail-item__label">
                  <User size={14} /> Created By
                </span>
                <span className="detail-item__value">{selectedItem.creator.name || selectedItem.creator.email}</span>
              </div>
            )}
            {selectedItem.updater && (
              <div className="detail-item">
                <span className="detail-item__label">
                  <User size={14} /> Updated By
                </span>
                <span className="detail-item__value">{selectedItem.updater.name || selectedItem.updater.email}</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="modal-actions">
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
          <div className="modal-actions__group">
            <Button variant="outlined" onClick={handleDownload}>
              <Download size={16} />
              Download
            </Button>
            <Button variant="outlined" onClick={onVersions}>
              <History size={16} />
              Versions
            </Button>
            {selectedItem.status === 'published' && (
              <Button variant="outlined" onClick={onAssign}>
                <Users size={16} />
                Assign
              </Button>
            )}
            {!selectedItem.deleted_at && (
              <Button variant="primary" onClick={onEdit}>
                <Edit2 size={16} />
                Edit
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

ItemView.propTypes = {
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onVersions: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
}

export default ItemView
